import { Injectable } from '@angular/core';
import {
  CoreQuestionsFactoryService,
  CountriesService,
  EmitterService,
  FormQuestionsService,
  PopUpService,
  UtilsService
} from 'core/services';
import { BehaviorSubject, Observable } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import {
  CheckboxQuestion,
  DatetimeQuestion,
  Decorator,
  DropdownQuestion,
  HiddenQuestion,
  NumberQuestion,
  QuestionBase,
  QuestionGroup,
  TextboxQuestion
} from 'core/components';
import { Client } from 'core/models';
import { KeplerKpi, KeplerMatch } from '../models/KeplerMatch';
import { environment } from 'environments/environment';
import { ProfileService } from 'core/services/profile.service';

const MAX_KPIS = 5;

@Injectable()
export class KeplerDataQuestionsService implements FormQuestionsService {
  protected questions: QuestionBase<any>[] = [];
  protected infoDataQuestionGroup: QuestionGroup;
  protected configDataQuestionGroup: QuestionGroup;
  protected matchKpiListQuestion: QuestionBase<any>;
  protected matchSupervisorListQuestion: QuestionBase<any>;
  protected matchKpiTranslationsListQuestion: QuestionBase<any>;
  protected playerNotificationsDataQuestionGroup: QuestionGroup;
  protected translationQuestionGroup: QuestionGroup;
  protected formData: any = {};
  protected form: UntypedFormGroup;
  protected questionsSubject = new BehaviorSubject<QuestionBase<any>[]>([]);
  protected timezoneSubject = new BehaviorSubject(null);
  protected minEndDateSubject = new BehaviorSubject<Date | null>(null);
  protected maxStartDateSubject = new BehaviorSubject<Date | null>(null);

  private hasMatchStarted: boolean;
  private hasMatchEnded: boolean;
  private hasMatchPlayers: boolean = false;

  constructor(
    protected factory: CoreQuestionsFactoryService,
    protected countriesService: CountriesService,
    protected profileService: ProfileService,
    public translateService: TranslateService,
    public popupService: PopUpService
  ) {}

  public getFormData(): KeplerMatch {
    const KPIs: KeplerKpi[] = Object.values(this.form.value._kpiList).map((kpi: any, i: number) => {
      const { id, name, weight } = kpi;
      return { id, name, order: i + 1, weight: weight / 100 };
    });
    const supervisors: { email: string }[] = Object.values(this.form.value._supervisors)
      .filter((supervisor: { email: string }) => supervisor && supervisor.email)
      .map((supervisor: { email: string }) => ({ email: supervisor?.email }));
    const data = { ...this.form.value, supervisors, KPIs, languages: Object.keys(this.form.value._translations) };
    delete data._kpisValidator;
    delete data._supervisors;
    delete data._kpiList;
    delete data._translations;
    return data;
  }

  public getFormQuestions(params?: any): Observable<QuestionBase<any>[]> {
    this.onDataChanged();
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  public onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  public setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  /**
   * Create all questions of the match
   * @param params
   */
  public createQuestions(params: any = {}) {
    const { keplerMatch }: { keplerMatch: KeplerMatch } = params;

    const now = new Date().getTime();
    const matchStartTime = keplerMatch?.id ? new Date(keplerMatch.startDate).getTime() : null;
    const matchEndTime = keplerMatch?.id ? new Date(keplerMatch.endDate).getTime() : null;
    this.hasMatchStarted = matchStartTime && matchStartTime < now;
    this.hasMatchEnded = matchEndTime && matchEndTime < now;
    this.hasMatchPlayers = keplerMatch?.playerCount > 0;

    this.infoDataQuestionGroup = this.getKeplerInfoQuestions(params);
    this.configDataQuestionGroup = this.getKeplerConfigQuestions(params);
    this.playerNotificationsDataQuestionGroup = this.getKeplerPlayersNotificationsQuestions(params);
    const questions = [
      this.infoDataQuestionGroup,
      this.configDataQuestionGroup,
      this.playerNotificationsDataQuestionGroup
    ];
    this.questions = questions.filter((question) => question != null);
    this.questionsSubject.next(this.questions);
  }

  public getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  public getKeplerInfoQuestions(params: any): QuestionGroup {
    return new QuestionGroup({
      title: 'kepler.info.title',
      isSection: true,
      childQuestions: this.createKeplerInfoQuestions(params).filter((question) => question != null)
    });
  }

  /**
   * Create the Info section of the match
   * @param params
   * @private
   */
  private createKeplerInfoQuestions(params: any = {}): QuestionBase<any>[] {
    const { keplerMatch, client } = params;
    const clientId = keplerMatch?.clientId || client?.id;
    const partnerId = keplerMatch?.partnerId || client?.distributorId;
    const country = keplerMatch?.countryId || client?.countryId;
    const timezone = keplerMatch?.timezone || client?.timezone;

    return [
      new HiddenQuestion({
        key: 'clientId',
        value: clientId
      }),
      partnerId
        ? new HiddenQuestion({
            key: 'partnerId',
            value: partnerId
          })
        : null,
      new TextboxQuestion({
        key: 'name',
        label: 'kepler.info.name',
        placeholder: 'kepler.info.name',
        class: 'col-1',
        editable: !this.hasMatchEnded,
        value: keplerMatch?.name || null,
        validators: { required: true, maxLength: 100 }
      }),
      this.getCountryQuestion(params, country),
      this.getTimezoneQuestion(country, timezone)
    ].filter((q) => !!q);
  }

  private getCountryQuestion(params, country): QuestionBase<string> {
    const editable = !this.hasMatchStarted && this.profileService.canExecute('kepler.editMatchDates');
    return this.factory.getCountryQuestion({
      ...params,
      key: 'countryId',
      class: 'col-2',
      editable,
      value: country,
      validators: { required: true },
      eventHandlers: {
        onChange: (value) => this.onChangeCountry(value)
      }
    });
  }

  private getTimezoneQuestion(country, timezone): QuestionBase<string> {
    const editable = !this.hasMatchStarted && this.profileService.canExecute('kepler.editMatchDates');
    this.timezoneSubject.next(timezone);
    return this.factory.getTimezoneQuestion({
      country,
      class: 'col-2',
      value: timezone,
      validators: { required: true },
      editable,
      eventHandlers: {
        onChange: (value) => {
          this.timezoneSubject.next(value);
        }
      }
    });
  }

  /**
   * Set the selected country, timezone and bonus that depends on client
   * @param country key
   */
  private onChangeCountry(country: string): void {
    // Set the timezone dropdown
    const timezone = this.countriesService.getDefaultTimezone(country);
    const timezoneQuestion = this.infoDataQuestionGroup.getChildQuestion('timezone');
    timezoneQuestion.options = this.countriesService.getTimezoneOptions(country);
    timezoneQuestion.control.setValue(timezone);
  }

  private getKeplerConfigQuestions(params: any): QuestionGroup {
    return new QuestionGroup({
      title: 'kepler.info.configTitle',
      isSection: true,
      childQuestions: this.createKeplerConfigQuestions(params).filter((question) => question != null)
    });
  }

  /**
   * Create the Config section of the match
   * @param params
   * @private
   */
  private createKeplerConfigQuestions(params: any = {}): QuestionBase<any>[] {
    const { keplerMatch, client } = params;

    this.minEndDateSubject.next(
      keplerMatch?.startDate && (!this.hasMatchStarted || this.hasMatchEnded)
        ? new Date(keplerMatch.startDate)
        : new Date()
    );
    this.maxStartDateSubject.next(keplerMatch?.endDate ? new Date(keplerMatch.endDate) : null);

    this.matchKpiListQuestion = this.getKpisQuestions(keplerMatch, client);
    this.matchSupervisorListQuestion = this.getSupervisorsQuestions(keplerMatch);
    this.matchKpiTranslationsListQuestion = this.getKpiTranslationsQuestions(keplerMatch, client);
    return [
      this.getStartDateQuestion(keplerMatch),
      this.getEndDateQuestion(keplerMatch),
      this.getLangQuestion(keplerMatch, client),
      new CheckboxQuestion({
        key: 'isAnonymous',
        class: 'col-1',
        label: 'kepler.info.isAnonymous',
        editable: !keplerMatch,
        value: keplerMatch?.isAnonymous,
        tooltip: 'kepler.info.isAnonymousTooltip'
      }),
      new Decorator({ separator: true }),
      this.matchKpiListQuestion,
      new CheckboxQuestion({
        key: 'shouldDisplayObjectives',
        class: 'col-1',
        label: 'kepler.info.showGoals',
        editable: !this.hasMatchStarted,
        value: keplerMatch ? !!keplerMatch.shouldDisplayObjectives : true,
        eventHandlers: {
          onChange: (value) => {
            this.configDataQuestionGroup.getChildQuestion('defaultObjectivesDuration').setDisabled(!value);
            if (!value) {
              this.configDataQuestionGroup.getChildQuestion('defaultObjectivesDuration').value = null;
            }
          }
        }
      }),
      this.getKpiLimitQuestion(keplerMatch),
      new CheckboxQuestion({
        key: 'shouldDisplayKPIs',
        class: 'col-1',
        label: 'kepler.info.showKpis',
        editable: !this.hasMatchStarted,
        value: keplerMatch ? !!keplerMatch.shouldDisplayKPIs : true
      }),
      new Decorator({ separator: true }),
      this.matchKpiTranslationsListQuestion,
      new Decorator({ separator: true }),
      this.matchSupervisorListQuestion
    ];
  }

  private getLangQuestion(match: any, client: Client): QuestionBase<any> {
    const { keplerLanguages } = environment;
    const lang =
      match?.lang || (client?.lang && keplerLanguages.some((lang) => lang.key === client.lang) ? client.lang : 'en');
    return new DropdownQuestion({
      key: 'lang',
      label: 'nouns.language',
      placeholder: 'actions.selectLanguage',
      class: 'col-2',
      value: lang,
      options: keplerLanguages.map(({ key, value }) => ({ key, value: this.translateService.instant(value) })),
      editable: !match?.id,
      validators: { required: true },
      eventHandlers: {
        onChange: (newLang) => {
          const keplerMatch: KeplerMatch = {
            ...this.getFormData(),
            languages: [newLang]
          };
          keplerMatch.KPIs = keplerMatch.KPIs.map((kpi) => {
            const changedKpi = { ...kpi, name: {} };
            changedKpi.name[newLang] = kpi.name[lang];
            return changedKpi;
          });
          this.createQuestions({ keplerMatch });
          this.form.markAsDirty();
        }
      }
    });
  }

  private getKpiLimitQuestion(match: any) {
    return new NumberQuestion({
      key: 'defaultObjectivesDuration',
      label: 'kepler.info.kpiLimitMessage',
      class: 'col-2-offset',
      tooltip: 'kepler.info.kpiLimitTooltip',
      disabled: match ? !match.shouldDisplayObjectives : false,
      editable: !this.hasMatchEnded,
      placeholder: 'kepler.info.kpiLimitPlaceholder',
      value: match?.defaultObjectivesDuration || '',
      validators: {
        required: true,
        minValue: { limit: 1 }
      }
    });
  }

  /**
   * Get the start date question for the match
   * @param match
   * @private
   */
  private getStartDateQuestion(match: any): DatetimeQuestion {
    const startDate = match?.startDate;
    const editable = !this.hasMatchStarted && this.profileService.canExecute('kepler.editMatchDates');

    const startDateQuestion = new DatetimeQuestion({
      key: 'startDate',
      label: 'nouns.startDate',
      placeholder: 'nouns.startDate',
      class: 'col-2',
      editable,
      timezone: this.timezoneSubject.asObservable(),
      maxDate: this.maxStartDateSubject.asObservable(),
      minDate: this.hasMatchStarted ? new Date(startDate) : new Date(),
      value: startDate ? startDate : null,
      validators: editable ? { required: true, invalidDate: true } : {},
      eventHandlers: editable
        ? {
            onChange: (matchStartDate) => {
              if (matchStartDate && matchStartDate instanceof Date && !isNaN(matchStartDate.getTime())) {
                this.minEndDateSubject.next(matchStartDate);
                // TODO: Delete or Uncommented when Cs's doesnt need to change the end date automatically
                /*const matchEndDate = DateTime.fromJSDate(matchStartDate)
                  .plus({ weeks: DEFAULT_MIN_WEEKS })
                  .minus({ second: 1 })
                  .toJSDate();
                startDate.form.controls['endDate'].setValue(matchEndDate);
                this.maxStartDateSubject.next(matchEndDate);*/
              }
            }
          }
        : {}
    });
    return startDateQuestion;
  }

  /**
   * Get the end date question for the match
   * @param match
   * @private
   */
  private getEndDateQuestion(match: any): DatetimeQuestion {
    const endDate = match?.endDate;
    const editable = !this.hasMatchEnded && this.profileService.canExecute('kepler.editMatchDates');

    const endDateQuestion = new DatetimeQuestion({
      key: 'endDate',
      label: 'nouns.endDate',
      placeholder: 'nouns.endDate',
      class: 'col-2',
      editable,
      timezone: this.timezoneSubject.asObservable(),
      minDate: this.minEndDateSubject.asObservable(),
      defaultHour: 23,
      defaultMinute: 59,
      defaultSeconds: 59,
      value: endDate ? endDate : null,
      validators: { required: true },
      eventHandlers: {
        onChange: (matchEndDate) => {
          this.maxStartDateSubject.next(matchEndDate);
        }
      }
    });
    return endDateQuestion;
  }

  /**
   * Get the questions of the KPIs in the match
   * @param match
   * @param client
   * @private
   */
  private getKpisQuestions(match: any, client: Client): QuestionBase<any> {
    const KPIs = match?.KPIs;
    const { keplerLanguages } = environment;
    const lang =
      match?.lang || (client?.lang && keplerLanguages.some((lang) => lang.key === client.lang) ? client.lang : 'en');
    const kpiListQuestion: QuestionGroup = new QuestionGroup({
      key: '_kpiList',
      class: 'form-custom-grid--item col-12',
      childQuestions: KPIs?.length
        ? KPIs.map((kpi) => this.createKpiQuestion(kpi, lang))
        : [this.createKpiQuestion(null, lang)]
    });

    const addKpiDecorator = this.getAddKpiDecorator(match, lang);
    const kpisValidatorDecorator = this.getKpisValidatorDecorator(kpiListQuestion, addKpiDecorator);

    const kpisQuestions = new QuestionGroup({
      title: 'kepler.info.kpiTitle',
      subtitle: 'kepler.kpiMaxLabel',
      isDropdown: true,
      isOpenCollapsible: true,
      disableGrid: true,
      gridClass: 'form-custom-grid',
      tooltip: 'kepler.info.kpiTooltip',
      childQuestions: [
        new Decorator({
          value: 'kepler.name',
          html: '<span class="mandatory">*</span>',
          class: 'form-custom-grid--item col-6',
          valueClass: 'gl-forms__input--title'
        }),
        new Decorator({
          value: 'kepler.kpiWeight',
          html: '<span class="mandatory">*</span>',
          class: 'form-custom-grid--item col-6 gap-left',
          valueClass: 'gl-forms__input--title'
        }),
        kpiListQuestion,
        kpisValidatorDecorator,
        addKpiDecorator
      ]
    });
    return kpisQuestions;
  }

  private getKpisValidatorDecorator(
    kpiListQuestion: QuestionBase<any>,
    addKpiDecorator: QuestionBase<any>
  ): QuestionBase<any> {
    return new Decorator({
      key: '_kpisValidator',
      isDefaultClass: false,
      valueClass: 'hidden-group-question',
      value: true,
      validators: {
        function: () => {
          const kpis = Object.values(this.form.controls._kpiList.value);
          const kpisControl = this.form.controls._kpiList;
          this.validateTranslations();
          if (!kpiListQuestion.childQuestions.length) {
            return {
              kpisError: {
                label: 'kepler.info.kpiErrorNoKpis'
              }
            };
          }
          addKpiDecorator.setDisabled(kpiListQuestion.childQuestions.length >= MAX_KPIS);
          if (kpisControl.dirty) {
            const totalWeight = kpis.reduce((tot: number, kpi: any) => tot + parseInt(kpi.weight || 0), 0);
            if (totalWeight !== 100) {
              return {
                kpisError: {
                  label: 'kepler.info.kpiErrorWeightMessage'
                }
              };
            }
          }
          return null;
        }
      }
    });
  }

  private getAddKpiDecorator(match: any, lang?): QuestionBase<any> {
    const isEditionMode = !!(match && match.id && match.playerCount);
    return new Decorator({
      class: 'form-custom-grid--item col-12  margin-top__form',
      hidden: this.hasMatchEnded,
      label: 'kepler.info.addKpi',
      buttonType: 'tertiary',
      iconClass: 'glicon-plus-circle',
      disabled: match?.KPIs?.length >= MAX_KPIS,
      order: 99,
      eventHandlers: {
        click: (ev, question) => {
          const kpiListQuestion = question.parent.getChildQuestion('_kpiList');
          if (kpiListQuestion.childQuestions.length < MAX_KPIS) {
            this.requestConfirmAddKpi(isEditionMode).then((accept) => {
              if (accept) {
                kpiListQuestion.childQuestions = [
                  ...kpiListQuestion.childQuestions,
                  this.createKpiQuestion(null, lang)
                ];
                this.form.controls._kpisValidator.updateValueAndValidity();
              }
            });
          }
        }
      }
    });
  }

  private requestConfirmAddKpi(isEditionMode) {
    return isEditionMode
      ? new Promise((resolve) => {
          this.popupService
            .showConfirm({
              title: this.translateService.instant('kepler.addKpi'),
              text: this.translateService.instant('kepler.addKpiMessage'),
              size: 'x-small',
              confirmButtonText: this.translateService.instant('kepler.info.addKpi')
            })
            .then(() => resolve(true))
            .catch(() => resolve(false));
        })
      : Promise.resolve(true);
  }

  private createKpiQuestion(kpi: any = {}, lang?: string): QuestionGroup {
    const { name = null, weight = null, id } = kpi || {};
    const key = `_kpi_${UtilsService.createGuid()}`;
    const isEditionMode = !!id;
    let nameQuestions: Array<any> = [];
    if (name) {
      const langs = Object.keys(name).filter((k) => k !== lang);
      nameQuestions = langs.map((language) => {
        return new HiddenQuestion({
          key: language,
          value: name[language]
        });
      });
    }
    nameQuestions.push(
      new TextboxQuestion({
        key: lang,
        placeholder: 'kepler.name',
        editable: !this.hasMatchEnded,
        value: name ? name[lang] : '',
        validators: {
          required: true,
          function: (control) => {
            const kpis = this.form.controls._kpiList.value;
            const kpiKeys: any[] = Object.keys(kpis).filter((kpiKey) => kpiKey !== key);
            if (kpiKeys.some((kpiKey) => kpis[kpiKey].name && kpis[kpiKey].name[lang] === control.value)) {
              return {
                kpisError: {
                  label: 'kepler.info.kpiErrorDuplicated'
                }
              };
            }
            return null;
          }
        }
      })
    );
    const kpiQuestions: QuestionGroup = new QuestionGroup({
      key,
      disableGrid: true,
      gridClass: 'form-custom-grid',
      childQuestions: [
        new QuestionGroup({
          key: 'name',
          class: 'form-custom-grid--item col-6 gap-right',
          disableGrid: true,
          childQuestions: nameQuestions
        }),
        new NumberQuestion({
          key: 'weight',
          class: 'form-custom-grid--item col-3 gap-left',
          editable: !this.hasMatchEnded,
          placeholder: '00',
          value: weight ? weight * 100 : weight,
          validators: {
            required: true,
            minValue: { limit: 0 },
            maxValue: { limit: 100 }
          },
          eventHandlers: {
            onChange: () => {
              kpiQuestions.getChildQuestion('name').control.updateValueAndValidity();
              this.form.controls._kpisValidator.updateValueAndValidity();
            }
          }
        }),
        id
          ? new HiddenQuestion({
              key: 'id',
              value: id
            })
          : null,
        new Decorator({
          label: 'kepler.info.deleteKpi',
          buttonType: 'icon',
          iconClass: 'glicon-trash',
          class: 'form-custom-grid--item align-end col-3',
          isDefaultClass: false,
          hidden: this.hasMatchEnded,
          eventHandlers: {
            click: () => {
              if (this.hasMatchPlayers) {
                this.requestConfirmDeleteKpi(isEditionMode).then((accept) => {
                  if (accept) {
                    this.deleteKpi(kpiQuestions, key);
                  }
                });
              } else {
                this.deleteKpi(kpiQuestions, key);
              }
            }
          }
        })
      ].filter((q) => !!q)
    });
    return kpiQuestions;
  }

  /**
   * Get the questions of the kpis translations in the match
   * @param match
   * @param client
   * @private
   */
  private getKpiTranslationsQuestions(match: any, client: Client): QuestionBase<any> {
    const { keplerLanguages } = environment;
    const lang =
      match?.lang || (client?.lang && keplerLanguages.some((lang) => lang.key === client.lang) ? client.lang : 'en');

    const translations = match?.languages ?? [lang];
    this.translationQuestionGroup = new QuestionGroup({
      key: '_translations',
      disableGrid: true,
      childQuestions: translations?.length
        ? translations.map((translation) => this.createKpiTranslationQuestion(translation, lang, translations))
        : []
    });
    const translationQuestions = new QuestionGroup({
      title: 'kepler.info.kpiTranslationsTitle',
      disableGrid: true,
      isDropdown: true,
      isOpenCollapsible: true,
      childQuestions: [
        this.translationQuestionGroup,
        new Decorator({
          hidden: this.hasMatchEnded,
          label: 'kepler.info.addKpiTranslation',
          iconClass: 'glicon-plus-circle',
          buttonType: 'tertiary',
          disabled: translations?.length === keplerLanguages?.length,
          eventHandlers: {
            click: () => {
              const kpiList = this.form.value._kpiList;
              const lang = this.form.value.lang;
              const data = {
                kpis: [...Object.keys(kpiList)].map((x) => ({
                  id: x,
                  name: kpiList[x].name
                })),
                source: lang,
                dest: null,
                translations
              };
              EmitterService.get('translateKeplerKpi').emit(data);
            }
          }
        })
      ]
    });
    return translationQuestions;
  }

  /**
   * Create a kpi translations question
   * @param translation
   * @param baseLang
   * @param translations
   * @private
   */
  private createKpiTranslationQuestion(translation: string, baseLang: string, translations: string[]): QuestionGroup {
    const translationsValueQuestionGroup = [
      new Decorator({
        key: translation,
        class: 'form-custom-grid--item col-10',
        designSystem: true,
        isDefaultClass: false,
        html: `<span>${this.translateService.instant('languageCodes.' + translation)}</span>`,
        validators: {
          function: (control, question) => {
            const kpiList: any[] = Object.values(this.form.controls._kpiList.value);
            if (kpiList.some((kpi) => !kpi.name || !kpi.name[translation])) {
              question.html = `
                <span>
                  ${this.translateService.instant('languageCodes.' + translation)}
                </span>
                <span class="lmstooltip margin-left-gap">
                  <span class="glicon-alert alert"></span>
                  <span class="lms-tooltip top center">
                    ${this.translateService.instant('kepler.info.kpiTranslationsTooltipInfo')}
                  </span>
                </span>`;
              return {
                kpisTranslationError: {
                  label: 'kepler.info.kpiErrorNoTranslation'
                }
              };
            }
            question.html = `<span>${this.translateService.instant('languageCodes.' + translation)}</span>`;
            return null;
          }
        }
      }),
      new Decorator({
        buttonType: 'icon',
        iconClass: 'glicon-edit',
        class: 'form-custom-grid--item col-1 gl-forms__card--actions',
        isDefaultClass: false,
        hidden: this.hasMatchEnded,
        eventHandlers: {
          click: () => {
            const kpiList = this.form.value._kpiList;
            const data = {
              kpis: [...Object.keys(kpiList)].map((x) => ({
                id: x,
                name: kpiList[x].name
              })),
              source: baseLang,
              dest: translation,
              translations
            };
            EmitterService.get('translateKeplerKpi').emit(data);
          }
        }
      }),
      new Decorator({
        buttonType: 'icon',
        iconClass: 'glicon-trash',
        class: 'form-custom-grid--item col-1 gl-forms__card--actions',
        isDefaultClass: false,
        hidden: this.hasMatchEnded,
        eventHandlers: {
          click: () =>
            this.requestConfirmDeleteKpiTranslation(translation).then((accept) => {
              if (accept) {
                this.deleteTranslations(translation);
              }
            })
        }
      })
    ];

    const translationsQuestionGroup = new QuestionGroup({
      disableGrid: true,
      gridClass: 'form-custom-grid gl-forms__card',
      hidden: translation === baseLang,
      childQuestions: translationsValueQuestionGroup
    });
    return translationsQuestionGroup;
  }

  private requestConfirmDeleteKpi(isEditionMode) {
    return isEditionMode
      ? new Promise((resolve) => {
          this.popupService
            .showConfirm({
              title: this.translateService.instant('kepler.deleteKpi'),
              text: this.translateService.instant('kepler.deleteKpiMessage'),
              size: 'x-small',
              confirmButtonText: this.translateService.instant('kepler.info.deleteKpi')
            })
            .then(() => resolve(true))
            .catch(() => resolve(false));
        })
      : Promise.resolve(true);
  }

  private requestConfirmDeleteKpiTranslation(lang: string) {
    return new Promise((resolve) => {
      this.popupService
        .showConfirm({
          title: this.translateService.instant('kepler.deleteKpiTranslation'),
          text: this.translateService.instant('kepler.deleteKpiTranslationMessage', {
            language: this.translateService.instant('languageCodes.' + lang)
          }),
          size: 'small',
          confirmButtonText: this.translateService.instant('kepler.info.deleteKpiTranslation')
        })
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });
  }

  /**
   * Get the questions of the supervisors in the match
   * @private
   * @param match
   */
  private getSupervisorsQuestions(match: any): QuestionBase<any> {
    const supervisors = match?.supervisors;

    const supervisorListQuestion = new QuestionGroup({
      key: '_supervisors',
      childQuestions: supervisors?.length
        ? supervisors.map((supervisor, index) => this.createSupervisorQuestion(supervisor, index === 0))
        : [this.createSupervisorQuestion(null, true)]
    });

    return new QuestionGroup({
      title: 'kepler.info.supervisorTitle',
      isDropdown: true,
      isOpenCollapsible: true,
      tooltip: 'kepler.info.supervisorTooltip',
      childQuestions: [
        supervisorListQuestion,
        new Decorator({
          hidden: this.hasMatchEnded,
          label: 'kepler.info.addSupervisor',
          buttonType: 'tertiary',
          iconClass: 'glicon-plus-circle',
          eventHandlers: {
            click: () => {
              const isFirst = !supervisorListQuestion.childQuestions.length;
              supervisorListQuestion.childQuestions = [
                ...supervisorListQuestion.childQuestions,
                this.createSupervisorQuestion(null, isFirst)
              ];
            }
          }
        })
      ]
    });
  }

  /**
   * Create a Supervisor question
   * @param supervisor
   * @param isFirstSupervisor
   * @private
   */
  private createSupervisorQuestion(supervisor: any = {}, isFirstSupervisor: boolean): QuestionGroup {
    const email = supervisor?.email;
    const key = `_supervisor_${UtilsService.createGuid()}`;

    const supervisorQuestionGroup = new QuestionGroup({
      key,
      disableGrid: true,
      gridClass: 'form-custom-grid',
      childQuestions: [
        new TextboxQuestion({
          key: 'email',
          type: 'email',
          label: isFirstSupervisor ? 'kepler.info.email' : null,
          placeholder: 'kepler.info.email',
          class: 'form-custom-grid--item col-9',
          editable: !email,
          value: email,
          validators: { mail: true }
        }),
        new Decorator({
          buttonType: 'icon',
          iconClass: 'glicon-trash',
          class: `form-custom-grid--item align-end col-3 ${isFirstSupervisor ? 'margin-top__form' : ''}`,
          isDefaultClass: false,
          hidden: this.hasMatchEnded,
          eventHandlers: {
            click: () => {
              const parent = supervisorQuestionGroup.parent;
              parent.childQuestions = parent.childQuestions.filter((s) => s.key !== key);
              (parent.control as UntypedFormGroup).removeControl(key);
              this.form.markAsDirty();
            }
          }
        })
      ]
    });
    return supervisorQuestionGroup;
  }

  /**
   * Get the questions of the notifications of players
   * @param params
   */
  public getKeplerPlayersNotificationsQuestions(params: any): QuestionGroup {
    const {
      keplerMatch: {
        notificationsCfg: {
          k1: { enabled: isK1Enabled = true } = {},
          k2: { enabled: isK2Enabled = true } = {},
          k3: { enabled: isK3Enabled = true } = {}
        } = {}
      } = {}
    } = params;
    return new QuestionGroup({
      key: 'notificationsCfg',
      title: 'kepler.info.playerNotificationTitle',
      isSection: true,
      childQuestions: [
        new QuestionGroup({
          key: 'k1',
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              class: 'col-1',
              label: 'kepler.info.sendStartNotification',
              editable: !this.hasMatchEnded,
              value: isK1Enabled
            })
          ]
        }),
        new QuestionGroup({
          key: 'k2',
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              class: 'col-1',
              label: 'kepler.info.sendKpiNotification',
              editable: !this.hasMatchEnded,
              value: isK2Enabled
            })
          ]
        }),
        new QuestionGroup({
          key: 'k3',
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              class: 'col-1',
              label: 'kepler.info.sendEndNotification',
              editable: !this.hasMatchEnded,
              value: isK3Enabled
            })
          ]
        })
      ]
    });
  }

  public updateKpisTranslations(translation) {
    const keplerMatch: KeplerMatch = {
      ...this.getFormData()
    };

    keplerMatch.languages = !keplerMatch.languages.includes(translation.lang)
      ? [...keplerMatch.languages, translation.lang]
      : keplerMatch.languages;
    for (let index = 0; index < keplerMatch.KPIs.length; index++) {
      keplerMatch.KPIs[index].name[translation.lang] = translation.names[index];
    }
    this.createQuestions({ keplerMatch });
    this.form.markAsDirty();
  }

  public deleteTranslations(lang: string) {
    const keplerMatch: KeplerMatch = {
      ...this.getFormData()
    };
    keplerMatch.languages = keplerMatch.languages.filter((l) => l !== lang);
    for (let index = 0; index < keplerMatch.KPIs.length; index++) {
      delete keplerMatch.KPIs[index].name[lang];
    }
    this.createQuestions({ keplerMatch });
    this.form.markAsDirty();
  }

  public validateTranslations() {
    setTimeout(() => {
      this.translationQuestionGroup.childQuestions.forEach((child) => {
        child.childQuestions[0].control.updateValueAndValidity();
      });
    });
  }

  /**
   * Delete the KPIs of the questions
   * @param kpiQuestions
   * @param key
   * @private
   */
  private deleteKpi(kpiQuestions: QuestionGroup, key: string): void {
    const parent = kpiQuestions.parent;
    parent.childQuestions = parent.childQuestions.filter((s) => s.key !== key);
    (parent.control as UntypedFormGroup).removeControl(key);
    this.form.controls._kpiList.markAsDirty();
    this.form.controls._kpisValidator.updateValueAndValidity();
  }
}
